import { useEffect, useState, useSyncExternalStore, lazy, Suspense } from "react";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { useToast } from "@/hooks/use-toast";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ShoppingCart, User, Menu, LogOut, Package, Truck, Key, Store } from "lucide-react";
import { setupGlobalErrorHandlers } from "@/lib/errorReporter";
import { ErrorBoundary } from "@/components/error-boundary";
import { useSessionTracker } from "@/hooks/use-session-tracker";
import { useSessionRecorder } from "@/hooks/use-session-recorder";
import { useSessionMonitor } from "@/hooks/useSessionMonitor";
import logoImage from "@assets/openbox_1762897846924.png";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { useCartStore } from "@/stores/cart-store";
import { useFilterStore } from "@/stores/filter-store";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { useAuth, clearSessionIndicator } from "@/hooks/useAuth";
import ReactGA from "react-ga4";

// Initialize GA4 once at module level to prevent duplicate trackers
// Use window to persist across hot module reloads
declare global {
  interface Window {
    __GA4_INITIALIZED__?: boolean;
  }
}

const initializeGA4 = () => {
  if (!window.__GA4_INITIALIZED__) {
    const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID;
    if (GA4_MEASUREMENT_ID) {
      ReactGA.initialize(GA4_MEASUREMENT_ID);
      console.log('[GA4] Initialized with measurement ID:', GA4_MEASUREMENT_ID);
      window.__GA4_INITIALIZED__ = true;
    } else {
      console.warn('[GA4] No measurement ID found. Set VITE_GA4_MEASUREMENT_ID environment variable.');
    }
  }
};

import Home from "@/pages/home";
import ShowroomLanding from "@/pages/showroom-landing";
import CategoryPage from "@/pages/category-page";
import ProductDetail from "@/pages/product-detail";
import Checkout from "@/pages/checkout";
// Heavy admin/account pages are loaded on demand via React.lazy so that
// anonymous shoppers visiting `/`, `/category/*`, or `/product/*` do NOT
// download the admin/warehouse JavaScript bundle. (Task #180.)
const AdminDashboard = lazyWithRetry(() => import("@/pages/admin"));
const EditProduct = lazyWithRetry(() => import("@/pages/edit-product"));
const OrderDetail = lazyWithRetry(() => import("@/pages/order-detail"));
import UserAccount from "@/pages/user-account";
const WalletPage = lazyWithRetry(() => import("@/pages/wallet"));
const Orders = lazyWithRetry(() => import("@/pages/orders"));
const CustomerDetail = lazyWithRetry(() => import("@/pages/customer-detail"));
const AdminFreight = lazyWithRetry(() => import("@/pages/AdminFreight"));
const AdminEmails = lazyWithRetry(() => import("@/pages/AdminEmails"));
const EmailOutreach = lazyWithRetry(() => import("@/pages/EmailOutreach"));
const AdminStagedImports = lazyWithRetry(() => import("@/pages/admin-staged-imports"));
const AdminEnrichmentDetail = lazyWithRetry(() => import("@/pages/admin-enrichment-detail"));
const AdminCreateProduct = lazyWithRetry(() => import("@/pages/admin-create-product"));
const AdminPalletConstituents = lazyWithRetry(() => import("@/pages/admin-pallet-constituents"));
const AdminParticlesEnrichment = lazyWithRetry(() => import("@/pages/admin-particles-enrichment"));
const AdminInboundReturns = lazyWithRetry(() => import("@/pages/admin-inbound-returns"));
const StyleDemo = lazyWithRetry(() => import("@/pages/style-demo"));
import NotFound from "@/pages/not-found";
import LoginPage from "@/pages/login";
import SplashPage from "@/pages/splash";
import { Redirect } from "wouter";
import SignupPage from "@/pages/signup";
import SetupAccountPage from "@/pages/setup-account";
import GradingPage from "@/pages/grading";
import ForgotPasswordPage from "@/pages/forgot-password";
import SetPasswordPage from "@/pages/set-password";
import SchedulePickupPage from "@/pages/schedule-pickup";
import ApiAccessPage from "@/pages/api-access";
import ApiDocsPage from "@/pages/api-docs";
import IntegrationsPage from "@/pages/integrations";
import ComingSoonPage from "@/pages/coming-soon";
import { withAuth } from "@/components/withAuth";
import { withRoleGuard } from "@/components/withRoleGuard";
import { ContactWidget } from "@/components/contact-widget";
import { MasqueradeBanner } from "@/components/masquerade-banner";
import { SiteAnnouncementBanner } from "@/components/site-announcement-banner";
import TermsPage from "@/pages/terms";

// Wrap protected pages with authentication
// Note: Home, ProductDetail, and Checkout are public - guests can browse without login
// Checkout handles auth prompting internally when user tries to place order
const ProtectedUserAccount = withAuth(UserAccount);
const ProtectedWallet = withAuth(WalletPage);
const ProtectedOrders = withAuth(Orders);
const ProtectedBuyerOrderDetail = withAuth(OrderDetail); // Buyer-accessible order detail
const ProtectedIntegrations = withAuth(IntegrationsPage);
// Note: ApiAccessPage and GradingPage are public - no auth wrapper needed

// Admin-only pages - require admin role
const ProtectedAdminDashboard = withRoleGuard(AdminDashboard, { requireAdmin: true });
const ProtectedEditProduct = withRoleGuard(EditProduct, { requireAdmin: true });
const ProtectedOrderDetail = withRoleGuard(OrderDetail, { requireAdmin: true });
const ProtectedCustomerDetail = withRoleGuard(CustomerDetail, { requireAdmin: true });
const ProtectedAdminFreight = withRoleGuard(AdminFreight, { requireAdmin: true });
const ProtectedAdminEmails = withRoleGuard(AdminEmails, { requireAdmin: true });
const ProtectedAdminStagedImports = withRoleGuard(AdminStagedImports, { requireAdmin: true });
const ProtectedAdminEnrichmentDetail = withRoleGuard(AdminEnrichmentDetail, { requireAdmin: true });
const ProtectedAdminCreateProduct = withRoleGuard(AdminCreateProduct, { requireAdmin: true });
const ProtectedAdminParticles = withRoleGuard(AdminParticlesEnrichment, { allowedRoles: ['SUPER_ADMIN'] });
const ProtectedAdminPalletConstituents = withRoleGuard(AdminPalletConstituents, { requireAdmin: true });
const ProtectedAdminInboundReturns = withRoleGuard(AdminInboundReturns, { requireAdmin: true });
const ProtectedStyleDemo = withRoleGuard(StyleDemo, { requireAdmin: true });
const ProtectedEmailOutreach = withRoleGuard(EmailOutreach, { requireAdmin: true });

const ProtectedNotFound = withAuth(NotFound);

// Note: SignupPage is NOT wrapped with withAuth since it's a public route

function Navigation() {
  const { getItemCount, loadCart, resetCart, currentUserId } = useCartStore();
  const [location, setLocation] = useLocation();
  const { user, isAuthenticated, isLoading } = useAuth();
  const { getShowroomUrl } = useFilterStore();
  const itemCount = getItemCount();
  // Load cart when user authenticates or changes, reset when logged out
  useEffect(() => {
    const userId = user?.id;

    if (!isAuthenticated || !userId) {
      // User logged out or not authenticated - reset cart
      if (currentUserId !== null) {
        resetCart();
      }
    } else if (userId !== currentUserId) {
      // User logged in or switched accounts - load new cart
      loadCart();
    }
  }, [user?.id, isAuthenticated, currentUserId, loadCart, resetCart]);

  // Helper to check if user has admin role
  const isAdmin = user?.roles?.some(role => 
    ['SOHNEN_ADMIN', 'SHOPJIMMY_ADMIN', 'SUPER_ADMIN'].includes(role)
  );


  // Fetch admin badge counts from lightweight endpoint
  // Only fetch when auth is fully resolved AND user is confirmed as admin
  const { data: badgeCounts } = useQuery<{
    openOrdersCount: number;
    unfillableOrdersCount: number;
    pendingApplicationsCount: number;
    pendingApiCredentialsCount: number;
    stockNotificationsCount: number;
    stockNotificationsLatestCreatedAt: string | null;
    unresolvedErrorsCount: number;
    healthIssuesCount: number;
    total: number;
  }>({
    queryKey: ['/api/admin/badge-counts'],
    enabled: !isLoading && isAuthenticated === true && isAdmin === true,
    staleTime: 30000, // Cache for 30 seconds to reduce requests
  });
  
  const pendingApplicationsCount = badgeCounts?.pendingApplicationsCount || 0;
  const openOrdersCount = badgeCounts?.openOrdersCount || 0;

  // Stock notifications use a per-admin "seen since" indicator (see admin.tsx).
  // The server's `total` always sums the raw unnotified count, which leaks a
  // phantom red badge on the header Admin link even after the admin has viewed
  // the Stock Notifications tab and the sidebar count has cleared. Re-derive
  // the header total here using the same seen-aware logic so the two stay in
  // sync. Read the localStorage key directly each render — it's set by
  // admin.tsx when the tab is opened.
  const userId = (user as { id?: string } | null | undefined)?.id;
  // Reactive read of the per-user "stock notifications seen at" timestamp that
  // admin.tsx writes to localStorage. We subscribe to the native cross-tab
  // 'storage' event AND a custom in-tab 'admin-stock-notif-seen' event that
  // admin.tsx dispatches when it marks the tab as viewed, so the header badge
  // updates immediately without waiting for an incidental re-render.
  const stockNotifSeenAt = useSyncExternalStore(
    (notify) => {
      if (typeof window === 'undefined') return () => {};
      window.addEventListener('storage', notify);
      window.addEventListener('admin-stock-notif-seen', notify);
      return () => {
        window.removeEventListener('storage', notify);
        window.removeEventListener('admin-stock-notif-seen', notify);
      };
    },
    () => {
      if (typeof window === 'undefined' || !userId) return null;
      try {
        return window.localStorage.getItem(`admin:stockNotifSeenAt:${userId}`);
      } catch {
        return null;
      }
    },
    () => null,
  );
  const latestStockNotifAt = badgeCounts?.stockNotificationsLatestCreatedAt || null;
  const stockNotificationsBadge = (
    latestStockNotifAt && (!stockNotifSeenAt || latestStockNotifAt > stockNotifSeenAt)
  )
    ? (badgeCounts?.stockNotificationsCount || 0)
    : 0;
  const totalAdminBadgeCount =
    (badgeCounts?.openOrdersCount || 0) +
    (badgeCounts?.unfillableOrdersCount || 0) +
    (badgeCounts?.pendingApplicationsCount || 0) +
    (badgeCounts?.pendingApiCredentialsCount || 0) +
    (badgeCounts?.unresolvedErrorsCount || 0) +
    (badgeCounts?.healthIssuesCount || 0) +
    stockNotificationsBadge;

  const allNavItems = [
    { href: "/", label: "Showroom", active: location === "/", showWhen: 'always' }, // Show for all users (logged in or out)
    { href: "/grading", label: "Grading & Packaging", active: location === "/grading", showWhen: 'always' }, // Show for all users (logged in or out)
    { href: "/admin", label: "Admin", active: location.startsWith("/admin"), showWhen: 'admin' },
  ];

  // Filter navigation items based on authentication and user role
  const navItems = allNavItems.filter(item => {
    if (item.showWhen === 'always') {
      return true; // Show for everyone
    } else if (item.showWhen === 'guest') {
      return !isAuthenticated;
    } else if (item.showWhen === 'logged-in') {
      return isAuthenticated;
    } else if (item.showWhen === 'admin') {
      return isAdmin;
    }
    return false;
  });

  // Desktop nav items
  const DesktopNavItems = () => (
    <div className="ss-nav flex gap-1">
      {navItems.map((item) => (
        <div key={item.href} className="relative">
          <Link 
            href={item.href}
            className={cn(
              "px-3 py-2 rounded-md text-sm font-medium transition-colors",
              "hover:bg-blue-50 dark:hover:bg-blue-950/30",
              "focus-visible:ring-2 focus-visible:ring-brand-blue",
              item.active 
                ? "text-foreground font-semibold" 
                : "text-muted-foreground hover:text-foreground"
            )}
            data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {item.label}
          </Link>
          {item.label === 'Admin' && totalAdminBadgeCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-2 -right-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
              data-testid="badge-admin-total"
            >
              {totalAdminBadgeCount}
            </Badge>
          )}
        </div>
      ))}
    </div>
  );

  // Mobile nav items - full width with larger touch targets
  const MobileNavItems = ({ onClose }: { onClose?: () => void }) => (
    <div className="flex flex-col w-full">
      {navItems.map((item) => (
        <Link 
          key={item.href}
          href={item.href}
          onClick={onClose}
          className={cn(
            "mobile-nav-link flex items-center justify-between touch-target",
            item.active 
              ? "text-primary font-semibold bg-primary/5" 
              : "text-foreground hover:bg-muted"
          )}
          data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <span>{item.label}</span>
          {item.label === 'Admin' && totalAdminBadgeCount > 0 && (
            <Badge variant="destructive" className="h-5 min-w-[20px] rounded-full px-1.5">
              {totalAdminBadgeCount}
            </Badge>
          )}
        </Link>
      ))}
      
      {/* Mobile-only account links */}
      <div className="border-t border-border mt-2 pt-2">
        {isAuthenticated && user ? (
          <>
            <Link 
              href="/account" 
              onClick={onClose}
              className="mobile-nav-link flex items-center gap-3 touch-target text-foreground hover:bg-muted"
            >
              <User className="h-5 w-5" />
              <span>My Account</span>
            </Link>
            <Link 
              href="/orders" 
              onClick={onClose}
              className="mobile-nav-link flex items-center gap-3 touch-target text-foreground hover:bg-muted"
            >
              <Package className="h-5 w-5" />
              <span>My Orders</span>
            </Link>
            <Link 
              href="/api-access" 
              onClick={onClose}
              className="mobile-nav-link flex items-center gap-3 touch-target text-foreground hover:bg-muted"
            >
              <Key className="h-5 w-5" />
              <span>API Access</span>
            </Link>
            <Link 
              href="/integrations" 
              onClick={onClose}
              className="mobile-nav-link flex items-center gap-3 touch-target text-foreground hover:bg-muted"
            >
              <Store className="h-5 w-5" />
              <span>Integrations</span>
            </Link>
            <button 
              onClick={async () => {
                try {
                  const response = await fetch('/api/logout', { method: 'POST' });
                  if (response.ok) {
                    clearSessionIndicator();
                    window.location.href = '/';
                  }
                } catch (error) {
                  console.error('Logout failed:', error);
                }
              }}
              className="mobile-nav-link flex items-center gap-3 touch-target text-destructive hover:bg-destructive/10 w-full"
            >
              <LogOut className="h-5 w-5" />
              <span>Log out</span>
            </button>
          </>
        ) : (
          <>
            <Link 
              href="/login" 
              onClick={onClose}
              className="mobile-nav-link flex items-center gap-3 touch-target text-foreground hover:bg-muted"
            >
              <User className="h-5 w-5" />
              <span>Log In</span>
            </Link>
            <Link 
              href="/signup" 
              onClick={onClose}
              className="mobile-nav-link flex items-center gap-3 touch-target text-primary font-semibold hover:bg-primary/10"
            >
              <Package className="h-5 w-5" />
              <span>Sign Up</span>
            </Link>
          </>
        )}
      </div>
    </div>
  );

  return (
    <nav className="bg-card border-b border-border sticky top-0 z-50 shadow-sm">
      <div className="container-responsive">
        <div className="flex justify-between items-center h-14 md:h-16">
          {/* Logo - smaller on mobile */}
          <div className="flex items-center gap-4 md:gap-8">
            <Link href={getShowroomUrl()} className="flex items-center gap-2 shrink-0">
              <img src={logoImage} alt="OpenBox by ShopJimmy" width={160} height={40} className="h-8 md:h-10 w-auto object-contain" />
            </Link>
            {/* Desktop nav */}
            <div className="hidden md:flex gap-6">
              <DesktopNavItems />
            </div>
          </div>
          
          {/* Warranty message - desktop only */}
          {!isAuthenticated && (
            <div className="hidden lg:block flex-1 text-center px-4">
              <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-lime-500/10 dark:bg-lime-500/20 border border-lime-500/30">
                <span className="text-lime-700 dark:text-lime-400 font-semibold text-sm">
                  ✓ 1-Year Factory Warranty on all refrigerators
                </span>
              </div>
            </div>
          )}
          
          {/* Right side icons - properly sized for mobile */}
          <div className="flex items-center gap-1 sm:gap-2 md:gap-4">
            {/* Header search removed — each category page now has its own
                live-filtering search box at the top. Direct deep-links to
                `/showroom-classic?search=…` still work for legacy URLs. */}
            
            {/* Cart */}
            {itemCount > 0 && (
              <Link href="/checkout">
                <Button 
                  variant="ghost" 
                  aria-label={`Cart, ${itemCount} ${itemCount === 1 ? 'item' : 'items'}`}
                  className="h-10 w-10 md:h-11 md:w-11 p-0 text-foreground hover:text-foreground/80 relative touch-target" 
                  data-testid="button-cart"
                >
                  <Truck className="h-5 w-5 md:h-6 md:w-6" />
                  <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground rounded-full min-w-[18px] h-[18px] flex items-center justify-center text-[10px] md:text-xs font-medium px-1">
                    {itemCount}
                  </span>
                </Button>
              </Link>
            )}

            {/* User Account Menu - Desktop only */}
            <div className="hidden md:block">
              {isLoading ? (
                <Button variant="ghost" aria-label="Account" className="h-11 w-11 p-0 text-foreground">
                  <User className="h-6 w-6" />
                </Button>
              ) : isAuthenticated && user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      aria-label="User account menu"
                      className="h-11 w-11 p-0 text-foreground hover:text-foreground/80"
                      data-testid="button-user-menu"
                    >
                      {user.profileImageUrl ? (
                        <img 
                          src={user.profileImageUrl} 
                          alt={user.email || "User"} 
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <User className="h-6 w-6" />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">
                          {user.firstName && user.lastName 
                            ? `${user.firstName} ${user.lastName}` 
                            : user.email}
                        </p>
                        {user.email && (
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        )}
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link href="/account" data-testid="link-account">
                        <User className="mr-2 h-4 w-4" />
                        <span>My Account</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/orders" data-testid="link-orders">
                        <Package className="mr-2 h-4 w-4" />
                        <span>My Orders</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/api-access" data-testid="link-api-access">
                        <Key className="mr-2 h-4 w-4" />
                        <span>API Access</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/integrations" data-testid="link-integrations">
                        <Store className="mr-2 h-4 w-4" />
                        <span>Integrations</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={async () => {
                        try {
                          const response = await fetch('/api/logout', { method: 'POST' });
                          if (response.ok) {
                            clearSessionIndicator();
                            window.location.href = '/';
                          }
                        } catch (error) {
                          console.error('Logout failed:', error);
                        }
                      }}
                      data-testid="button-logout"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Log out</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      aria-label="Guest account menu"
                      className="h-11 w-11 p-0 text-foreground hover:text-foreground/80"
                      data-testid="button-guest-menu"
                    >
                      <User className="h-6 w-6" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      Guest Account
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link href="/login" data-testid="link-login">
                        <User className="mr-2 h-4 w-4" />
                        <span>Log In</span>
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/signup" data-testid="link-signup">
                        <Package className="mr-2 h-4 w-4" />
                        <span>Sign Up</span>
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Mobile hamburger menu */}
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" aria-label="Open menu" className="md:hidden h-10 w-10 p-0 text-foreground touch-target">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] sm:w-[320px] p-0">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
                <div className="flex flex-col h-full">
                  {/* Mobile menu header */}
                  <div className="p-4 border-b border-border">
                    <img src={logoImage} alt="OpenBox by ShopJimmy" className="h-8 w-auto object-contain" />
                    {isAuthenticated && user && (
                      <div className="mt-3 text-sm">
                        <p className="font-medium text-foreground">
                          {user.firstName && user.lastName 
                            ? `${user.firstName} ${user.lastName}` 
                            : user.email}
                        </p>
                        {user.email && user.firstName && (
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Mobile navigation */}
                  <div className="flex-1 overflow-y-auto py-2">
                    <MobileNavItems />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}

function Footer() {
  const footerSections = [
    {
      title: "Shop",
      links: [
        { href: "/", label: "All Products" },
        { href: "/?grades=A", label: "Class A" },
        { href: "/?grades=B%2B", label: "Class B+" },
        { href: "/?grades=B-", label: "Class B-" },
      ]
    },
    {
      title: "Support",
      links: [
        { href: "#", label: "Contact Us" },
        { href: "#", label: "Shipping Info" },
        { href: "#", label: "Returns" },
        { href: "#", label: "FAQ" },
      ]
    },
    {
      title: "Company",
      links: [
        { href: "#", label: "About Us" },
        { href: "#", label: "Careers" },
        { href: "/terms", label: "Terms of Service" },
        { href: "#", label: "Privacy Policy" },
      ]
    }
  ];

  return (
    <footer className="bg-card border-t border-border mt-8 md:mt-12">
      <div className="container-responsive py-6 md:py-8">
        {/* Mobile: stacked layout, Desktop: 4-column grid */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {/* Logo section - full width on mobile */}
          <div className="col-span-2 sm:col-span-2 md:col-span-1 text-center md:text-left mb-4 md:mb-0">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
              <img src={logoImage} alt="OpenBox by ShopJimmy" className="h-7 md:h-8 w-auto object-contain" />
            </div>
            <p className="text-xs md:text-sm text-muted-foreground">
              Premium appliance refurbishment & distribution
            </p>
          </div>
          
          {/* Footer link sections */}
          {footerSections.map((section) => (
            <div key={section.title} className="text-center sm:text-left">
              <h4 className="font-semibold text-foreground mb-2 md:mb-3 text-sm md:text-base">{section.title}</h4>
              <ul className="space-y-1.5 md:space-y-2 text-xs md:text-sm text-muted-foreground">
                {section.links.map((link) => (
                  <li key={link.label}>
                    {link.href.startsWith('#') ? (
                      <a href={link.href} className="hover:text-foreground transition-colors touch-target inline-block py-0.5">
                        {link.label}
                      </a>
                    ) : (
                      <Link href={link.href} className="hover:text-foreground transition-colors touch-target inline-block py-0.5">
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        
        {/* Copyright */}
        <div className="mt-6 md:mt-8 pt-4 md:pt-8 border-t border-border text-center text-xs md:text-sm text-muted-foreground">
          <p>&copy; 2025 OpenBox by ShopJimmy. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

function Router() {
  return (
    <Switch>
      {/* Public routes - login, signup, setup, catalog, and products */}
      {/* Legacy /splash route redirects to showroom */}
      <Route path="/splash">{() => <Redirect to="/" />}</Route>
      <Route path="/login" component={LoginPage} />
      <Route path="/signup" component={SignupPage} />
      <Route path="/setup-account" component={SetupAccountPage} />
      <Route path="/forgot-password" component={ForgotPasswordPage} />
      <Route path="/set-password" component={SetPasswordPage} />
      <Route path="/reset-password" component={SetPasswordPage} />
      
      {/* Coming Soon landing pages - must come before "/" to avoid prefix matching */}
      <Route path="/coming-soon/:slug" component={ComingSoonPage} />
      
      {/* Category-first navigation (Task #111) */}
      <Route path="/" component={ShowroomLanding} />
      <Route path="/showroom" component={ShowroomLanding} />
      <Route path="/category/:slug" component={CategoryPage} />
      {/* Legacy flat showroom — preserved for global search and admin links */}
      <Route path="/showroom-classic" component={Home} />
      <Route path="/product/:id" component={ProductDetail} />

      {/* Public grading page */}
      <Route path="/grading" component={GradingPage} />
      <Route path="/terms" component={TermsPage} />
      <Route path="/schedule-pickup/:token" component={SchedulePickupPage} />
      
      {/* Public API pages */}
      <Route path="/api-docs" component={ApiDocsPage} />
      <Route path="/api-access" component={ApiAccessPage} />

      {/* Protected routes */}
      <Route path="/checkout" component={Checkout} />
      <Route path="/account" component={ProtectedUserAccount} />
      <Route path="/account/wallet" component={ProtectedWallet} />
      <Route path="/integrations" component={ProtectedIntegrations} />
      <Route path="/orders" component={ProtectedOrders} />
      <Route path="/orders/:id" component={ProtectedBuyerOrderDetail} />
      <Route path="/admin" component={ProtectedAdminDashboard} />
      <Route path="/admin/freight" component={ProtectedAdminFreight} />
      <Route path="/admin/emails" component={ProtectedAdminEmails} />
      <Route path="/admin/staged-imports" component={ProtectedAdminStagedImports} />
      <Route path="/admin/products/create" component={ProtectedAdminCreateProduct} />
      <Route path="/admin/pallet-constituents" component={ProtectedAdminPalletConstituents} />
      <Route path="/admin/particles" component={ProtectedAdminParticles} />
      <Route path="/admin/inbound-returns" component={ProtectedAdminInboundReturns} />
      <Route path="/admin/warehouse/inbound-returns" component={ProtectedAdminInboundReturns} />
      <Route path="/admin/enrichment/:jobId" component={ProtectedAdminEnrichmentDetail} />
      <Route path="/admin/product/:id" component={ProtectedEditProduct} />
      <Route path="/admin/orders/:id" component={ProtectedOrderDetail} />
      <Route path="/admin/customers/:id" component={ProtectedCustomerDetail} />
      <Route path="/admin/outreach" component={ProtectedEmailOutreach} />
      <Route path="/style-demo" component={ProtectedStyleDemo} />

      {/* 404 fallback - requires authentication */}
      <Route component={ProtectedNotFound} />
    </Switch>
  );
}

function MasqueradeBannerWrapper() {
  const { user, isAuthenticated } = useAuth();
  
  if (!isAuthenticated || !(user as any)?.isMasquerading) {
    return null;
  }
  
  return <MasqueradeBanner user={user as any} />;
}

function ContactWidgetWrapper() {
  const { user, isAuthenticated } = useAuth();
  const [location] = useLocation();
  
  // Only show for authenticated B2B users, not on admin pages
  const isAdmin = user?.roles?.includes('ADMIN');
  const isOnAdminPage = location.startsWith('/admin');
  const isOnCheckout = location.startsWith('/checkout');
  const shouldShowWidget = isAuthenticated && !isAdmin && !isOnAdminPage && !isOnCheckout;
  
  if (!shouldShowWidget) return null;
  
  return <ContactWidget />;
}

function App() {
  const [location] = useLocation();
  const { toast } = useToast();
  
  // Initialize session tracking (captures clicks, page views, errors)
  const { getSessionToken } = useSessionTracker();
  
  // Initialize session replay recording (captures DOM snapshots, mouse movements, mutations)
  // Only record on non-admin pages to avoid recording admin activity
  const sessionToken = getSessionToken();
  const shouldRecord = !location.startsWith('/admin');
  useSessionRecorder(sessionToken || null, shouldRecord);
  
  // Session monitor - checks session validity periodically and on tab visibility change
  // Redirects to login if session expires while user is on a protected page
  useSessionMonitor({ enabled: true });

  // Initialize Google Analytics 4 and global error handlers (runs once at module level)
  useEffect(() => {
    initializeGA4();
    setupGlobalErrorHandlers();
  }, []);

  // Handle unsubscribe confirmation from invitation email link
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('unsubscribed') === 'true') {
      // Show toast message
      toast({
        title: "Unsubscribed",
        description: "You have been unsubscribed from our invitation emails.",
      });
      // Remove the query param from URL without reloading
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [toast]);

  // Handle auth redirect messages (403 forbidden, session expired, etc.)
  useEffect(() => {
    const authMessage = sessionStorage.getItem('auth_message');
    if (authMessage) {
      toast({
        title: "Access Denied",
        description: authMessage,
        variant: "destructive",
      });
      sessionStorage.removeItem('auth_message');
    }
  }, [toast]);

  // Track page views on route changes (skip admin pages)
  useEffect(() => {
    if (window.__GA4_INITIALIZED__ && !location.startsWith('/admin')) {
      ReactGA.send({ 
        hitType: "pageview", 
        page: location,
        title: document.title
      });
      // Track hostname for subdomain analysis
      ReactGA.event({
        category: 'Navigation',
        action: 'page_view',
        label: window.location.hostname,
        nonInteraction: true
      });
      console.log('[GA4] Page view tracked:', location, 'on', window.location.hostname);
    }
  }, [location]);

  // Hide header and footer on splash, login, signup, and password pages
  const hideHeaderFooter = location === '/splash' || location === '/login' || location === '/signup' || 
    location === '/forgot-password' || location.startsWith('/set-password') || location.startsWith('/reset-password');

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <ErrorBoundary componentName="App">
          <MasqueradeBannerWrapper />
          {!location.startsWith('/admin') && <SiteAnnouncementBanner />}
          <div className="min-h-screen bg-background text-foreground">
            {!hideHeaderFooter && <Navigation />}
            <main className="flex-1">
              <Suspense
                fallback={
                  <div className="min-h-[50vh] flex items-center justify-center text-muted-foreground text-sm">
                    Loading…
                  </div>
                }
              >
                <Router />
              </Suspense>
            </main>
            <ContactWidgetWrapper />
          </div>
        </ErrorBoundary>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;